import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, BookOpen, Clock, CheckCircle, Trash2 } from 'lucide-react'
import { motion } from 'framer-motion'
import { Button, Card, CardContent } from '@/components/ui'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import { useStudio } from '@/context/StudioContext'
import { UpgradeBanner } from '@/components/UpgradeBanner'
import type { StoryRecord } from '@/types/domain'

export function MyStories() {
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const { showToast, celebrate } = useToast()
  const { repository, readOnly } = useStudio()
  const [stories, setStories] = useState<StoryRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [canCreate, setCanCreate] = useState(true)
  const [showLimitBanner, setShowLimitBanner] = useState(false)

  // Load stories and check if new stories can be created
  useEffect(() => {
    async function loadStories() {
      if (!activeWriter) return

      try {
        const result = await repository.listStories(activeWriter.id)
        if (result.success) {
          setStories(result.data)
        } else {
          showToast('Could not load your stories. Please try again.', 'error')
        }

        // Check if new stories can be created
        const createAllowed = await repository.canCreateStory(activeWriter.id)
        setCanCreate(createAllowed)
      } catch (err) {
        console.error('Failed to load stories:', err)
        showToast('Could not load your stories. Please try again.', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadStories()
  }, [activeWriter, showToast, repository])

  const handleDeleteStory = async (e: React.MouseEvent, storyId: string) => {
    e.preventDefault() // Prevent navigation
    e.stopPropagation()

    if (readOnly) {
      showToast('Your subscription has lapsed. Renew to delete stories.', 'error')
      return
    }

    if (!confirm('Are you sure you want to delete this story?')) return

    try {
      const result = await repository.deleteStory(storyId)
      if (result.success) {
        setStories((prev) => prev.filter((s) => s.id !== storyId))
        showToast('Story deleted', 'success')
        // Re-check if we can create stories now
        if (activeWriter) {
          const createAllowed = await repository.canCreateStory(activeWriter.id)
          setCanCreate(createAllowed)
        }
      } else {
        showToast(result.error || 'Could not delete story. Please try again.', 'error')
      }
    } catch (err) {
      console.error('Failed to delete story:', err)
      showToast('Could not delete story. Please try again.', 'error')
    }
  }

  const handleCreateStory = async () => {
    if (!activeWriter || isCreating) return

    // Check if we can create a story (local mode has one-story limit)
    if (!canCreate) {
      setShowLimitBanner(true)
      return
    }

    if (readOnly) {
      showToast('Your subscription has lapsed. Renew to create stories.', 'error')
      return
    }

    setIsCreating(true)
    try {
      const result = await repository.createStory(activeWriter.id)
      if (result.success) {
        celebrate('New story created! Time to write!')
        // Navigate directly to the editor
        navigate(`/stories/${result.data.id}`)
      } else {
        if (result.code === 'LIMIT_REACHED') {
          setShowLimitBanner(true)
        } else {
          showToast(result.error || 'Could not create story. Please try again.', 'error')
        }
      }
    } catch (err) {
      console.error('Failed to create story:', err)
      showToast('Could not create story. Please try again.', 'error')
    } finally {
      setIsCreating(false)
    }
  }

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Upgrade banner when at story limit */}
        {showLimitBanner && (
          <div className="mb-6">
            <UpgradeBanner
              variant="story-limit"
              onDismiss={() => setShowLimitBanner(false)}
            />
          </div>
        )}

        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="font-display text-[var(--font-size-kid-2xl)] font-bold text-[var(--color-kids-text)] mb-2">
              My Stories
            </h1>
            <p className="text-[var(--color-kids-text-muted)] text-lg">
              {stories.length === 0
                ? 'Start your first adventure!'
                : `You have ${stories.length} stor${stories.length === 1 ? 'y' : 'ies'}`}
            </p>
          </div>

          <Button
            onClick={handleCreateStory}
            size="lg"
            disabled={isCreating}
            className="flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {isCreating ? 'Creating...' : 'New Story'}
          </Button>
        </motion.div>

        {/* Stories grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-kids-primary)]" />
          </div>
        ) : stories.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="max-w-md mx-auto">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-[var(--color-kids-primary)]20 rounded-full flex items-center justify-center">
                  <BookOpen className="w-10 h-10 text-[var(--color-kids-primary)]" />
                </div>
                <h2 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-2">
                  No stories yet
                </h2>
                <p className="text-[var(--color-kids-text-muted)] mb-6">
                  Every great writer starts with their first story. Ready to begin yours?
                </p>
                <Button onClick={handleCreateStory} disabled={isCreating}>
                  <Plus className="w-5 h-5 mr-2" />
                  Create Your First Story
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stories.map((story, index) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group relative">
                  <Link to={`/stories/${story.id}`}>
                    <CardContent className="p-6">
                      {/* Emoji cover */}
                      <div className="w-16 h-16 mb-4 bg-[var(--color-kids-surface-alt)] rounded-[var(--radius-kid)] flex items-center justify-center text-4xl group-hover:scale-110 transition-transform">
                        {story.coverEmoji}
                      </div>

                      {/* Title */}
                      <h3 className="font-display text-lg font-bold text-[var(--color-kids-text)] mb-2 line-clamp-2">
                        {story.title}
                      </h3>

                      {/* Meta info */}
                      <div className="flex items-center gap-4 text-sm text-[var(--color-kids-text-muted)]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatRelativeTime(story.updatedAt)}
                        </span>
                        <span>{story.wordCount} words</span>
                      </div>

                      {/* Status badge */}
                      <div className="mt-4">
                        {story.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--color-kids-success)]20 text-[var(--color-kids-success)] text-sm font-medium rounded-full">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--color-kids-primary)]20 text-[var(--color-kids-primary)] text-sm font-medium rounded-full">
                            In Progress
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Link>
                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDeleteStory(e, story.id)}
                    className="absolute top-3 right-3 p-2 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-100 text-[var(--color-kids-text-muted)] hover:text-red-500 transition-all"
                    title="Delete story"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
    </div>
  )
}
